import type { SqlImport } from '@/services/db/sql-import/entity';
import { ImportType } from '@/services/db/sql-import/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import type { SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { isEmptyString } from '@/utils/string';

/** What a people import targets, as the summary names it. */
export const PEOPLE_SYNC_TARGET = 'People';
/** What an object import targets when its object type cannot be resolved. */
export const OBJECTS_SYNC_TARGET = 'Objects';

/**
 * What an import writes to: "People", or the object type's name, or "Objects"
 * when the import has an object type the workspace no longer lists.
 */
export function sqlImportSyncTarget(
  sqlImport: { import_type: SqlImport['import_type']; object_type_id?: SqlImport['object_type_id'] | null },
  objectTypes: readonly Pick<ObjectType, 'id' | 'name'>[],
): string {
  if (sqlImport.import_type !== ImportType.object) {
    return PEOPLE_SYNC_TARGET;
  }
  const objectType = objectTypes.find((candidate) => candidate.id === sqlImport.object_type_id);
  return objectType?.name ?? OBJECTS_SYNC_TARGET;
}

/** The analytics bucket for a sync target: people, or anything else. */
export function sqlImportSyncTargetKind(syncTarget: string): 'People' | 'Objects' {
  return syncTarget === PEOPLE_SYNC_TARGET ? 'People' : 'Objects';
}

/** How a database is named in prose: its name, or for BigQuery its project. */
export function sqlImportDatabaseLabel(database: Pick<SqlImportDatabase, 'name' | 'project_id'>): string {
  return isEmptyString(database.name) ? database.project_id : database.name;
}
